import gql from 'graphql-tag';

import { appsPerPage } from 'config';

const ratingSummary = `
  ratingSummary {
    numRatings
    averageStars
  }
`;

// Fields needed to show previews
const previewAppFields = `
  id
  name
  type
  isPremium
  icon {
    uri
    width
    height
  }
  developer {
    id,
    name
  }
  banners: screenshots {
    uri
    width
    height
  }
  ${ratingSummary}
`;

// Fields needed for update button
const updateButtonFields = `
  latestVersion {
    buildId
    requestedPermissions
    requiresWifi
  }
`;

// All latest version fields
const fullLatestVersionFields = `
  createdAt
  buildId
  versionString
  components {
    descriptor
    size
  }
  requestedPermissions
  requiresWifi
`;

const availabilityFields = `
  availability {
    deviceTypes {
      type
    }
  }
`;

const userRating = `
  userRating {
    stars
    updatedAt
  }
`;

const simpleApp = `
  ${ratingSummary}
  name
`;

// Fields needed to show app details
const fullAppFields = `
  ${previewAppFields}
  contactUrl
  contactEmail
  description
  isPaid
  isPrivate
  canRate
  hidePermissions
  bypassPrivateAppWarning
  ${availabilityFields}
  latestVersion {
    ${fullLatestVersionFields}
  }
  ${userRating}
  richDescription
  richDescriptionPosition
  learnMore
  learnMoreDeeplink
  compatibilityMode
  flags
  developer {
    id,
    name
  }
  parentApp {
    id
    name
    icon {
      uri
      width
      height
    }
    developer {
      id
      name
    }
    type
  }
`;

const installedAppStatusFields = `
  appId
  installStatus
  currentBuildId
  pendingBuildId
  stagedBuildId
  priority
  permissions {
    name
    granted
  }
  favorite
  active
  selected
`;

const installedTileStatusFields = `
  tileId
  tileProviderId
  position
  priority
`;

export const installedAppStatusQuery = gql`
  query installedAppStatusQuery($id: ID!, $wireId: ID!) {
    appStatus: appInstallStatus(id: $id, wireId: $wireId) {
      ${installedAppStatusFields}
    }
  }
`;

export const allInstalledAppsStatusQuery = gql`
  query allInstalledAppsStatusQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
    }
  }
`;

export const allInstalledAppsStatusWithTilesQuery = gql`
  query allInstalledAppsStatusQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      tileStatuses {
        ${installedTileStatusFields}
      }
    }
  }
`;

export const installedAppsQuery = gql`
  query installedAppsQuery($type: String) {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      apps(type: $type) {
        ${fullAppFields}
        isPrivate
      }
    }
  }
`;

export const installedAppsWithTilesQuery = gql`
  query installedAppsQuery($type: String) {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      tileStatuses {
        ${installedTileStatusFields}
      }
      apps(type: $type) {
        ${fullAppFields}
        isPrivate
      }
    }
  }
`;

export const installedClocksQuery = gql`
  query installedClocksQuery($type: String!) {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      apps(type: $type) {
        ${fullAppFields}
      }
    }

    favorites(type: $type) {
      ${fullAppFields}
    }
  }
`;

export const installedClocksWithTilesQuery = gql`
  query installedClocksQuery($type: String!) {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      tileStatuses {
        ${installedTileStatusFields}
      }
      apps(type: $type) {
        ${fullAppFields}
      }
    }

    favorites(type: $type) {
      ${fullAppFields}
    }
  }
`;

export const installedAppsAndClocksQuery = gql`
  query installedAppsAndClocksQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      apps {
        ${fullAppFields}
        ${availabilityFields}
        ${updateButtonFields}
      }
    }

    favorites(type: "CLOCK") {
      ${fullAppFields}
      ${updateButtonFields}
    }
  }
`;

export const installedAppsClocksAndTilesQuery = gql`
  query installedAppsAndClocksQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      tileStatuses {
        ${installedTileStatusFields}
      }
      apps {
        ${fullAppFields}
        ${availabilityFields}
        ${updateButtonFields}
      }
    }

    favorites(type: "CLOCK") {
      ${fullAppFields}
      ${updateButtonFields}
    }
  }
`;

export const installedAppsAndClocksWithGrantQuery = gql`
  query installedAppsAndClocksQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      apps {
        ${fullAppFields}
        ${availabilityFields}
        ${updateButtonFields}
      }
      hasGalleryGrant
    }

    favorites(type: "CLOCK") {
      ${fullAppFields}
      ${updateButtonFields}
    }
  }
`;

export const installedAppsClocksAndTilesWithGrantQuery = gql`
  query installedAppsAndClocksQuery {
    installedApps {
      appStatuses {
        ${installedAppStatusFields}
      }
      tileStatuses {
        ${installedTileStatusFields}
      }
      apps {
        ${fullAppFields}
        ${availabilityFields}
        ${updateButtonFields}
      }
      hasGalleryGrant
    }

    favorites(type: "CLOCK") {
      ${fullAppFields}
      ${updateButtonFields}
    }
  }
`;

export const allAppsQuery = gql`
  query allAppsQuery($type: String) {
    apps(type: $type) {
      ${previewAppFields}
    }
  }
`;

export const appQuery = gql`
  query appQuery($id: ID!) {
    app: app(id: $id) {
      ${fullAppFields}
    }
  }
`;

export const simpleAppQuery = gql`
  query appQuery($id: ID!) {
    app: app(id: $id) {
      ${simpleApp}
    }
  }
`;

export const searchAllAppsQuery = gql`
  query searchAllApps(
    $type: AppType,
    $text: String!,
    $limit: Int!,
    $pageKey: ID
  ) {
    searchResults: searchApps(
      criteria: {
        type: $type,
        text: $text
      },
      pageOptions: {
        pageKey: $pageKey,
        limit: $limit,
        numNextPages: 1
      }
    ) {
      apps {
        ${previewAppFields}
      }
      nextPages
    }
  }
`;

export const searchDeveloperAppsQuery = gql`
  query searchDeveloperAppsQuery(
    $type: AppType,
    $developerProfileId: ID!,
    $limit: Int!,
    $pageKey: ID,
  ) {
    searchResults: searchApps(
      criteria: {
        type: $type,
        developerProfileId: $developerProfileId
      },
      pageOptions: {
        pageKey: $pageKey,
        limit: $limit,
        numNextPages: 1
      }
    ) {
      apps {
        ${previewAppFields}
      }
      nextPages
    }
  }
`;

export const searchPreviewQuery = gql`
  query searchAllAppsPreview(
    $text: String!,
  ) {
    appSearchResults: searchApps(
      criteria: {
        type: APP,
        text: $text
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
    clockSearchResults: searchApps(
      criteria: {
        type: CLOCK,
        text: $text
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
    tileSearchResults: searchApps(
      criteria: {
        type: TILE,
        text: $text
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
  }
`;

export const searchDeveloperPreviewQuery = gql`
  query searchAllDeveloperAppsPreview(
    $developerProfileId: ID!,
  ) {
    appSearchResults: searchApps(
      criteria: {
        type: APP,
        developerProfileId: $developerProfileId
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
    clockSearchResults: searchApps(
      criteria: {
        type: CLOCK,
        developerProfileId: $developerProfileId
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
    tileSearchResults: searchApps(
      criteria: {
        type: TILE,
        developerProfileId: $developerProfileId
      },
      pageOptions: {
        limit: 4,
      }
    ) {
      apps {
        ${previewAppFields}
      }
    }
  }
`;

export const featuresAndUserQuery = gql`
  query featuresQuery {
    features {
      name
      enabled
      value
    }

    currentUser {
      id
      device {
        version
        productName
        maxFavoriteClocks
        maxInstalledClocks
      }
    }
  }
`;

export const rateApp = gql`
  mutation rateApp(
    $id: ID!
    $stars: Int!
  ) {
    rateApp(id: $id, stars: $stars) {
      ${fullAppFields}
    }
  }
`;

export const unrateApp = gql`
  mutation unrateApp(
    $id: ID!
  ) {
    unrateApp(id: $id) {
      ${fullAppFields}
    }
  }
`;

export const appInstallMutate = gql`
  mutation installApp(
    $id: ID!
    $wireId: ID!
    $permissions: [PermissionInput!]
  ) {
    installApp(id: $id, wireId: $wireId, permissions: $permissions) {
      appId
      installStatus
      installed
      permissions {
        name
        granted
      }
      favorite
      active
      selected
    }
  }
`;

export const tileInstallMutate = gql`
  mutation installTile($id: ID!, $permissions: [PermissionInput!]) {
    installTile(id: $id, permissions: $permissions) {
      appId
      installStatus
      installed
      permissions {
        name
        granted
      }
    }
  }
`;

export const tileUninstallMutate = gql`
  mutation uninstallTile($id: ID!) {
    uninstallTile(id: $id) {
      appId
      installStatus
      installed
    }
  }
`;

export const appPermissionMutate = gql`
  mutation setAppPermissions($id: ID!, $permissions: [PermissionInput!]!) {
    setAppPermissions(id: $id, permissions: $permissions) {
      appId
      permissions {
        name
        granted
      }
    }
  }
`;

export const appUninstallMutate = gql`
  mutation unInstallApp($id: ID!, $wireId: ID!) {
    uninstallApp(id: $id, wireId: $wireId) {
      appId
      installStatus
      installed
      favorite
      selected
      active
    }
  }
`;

export const repositionTilesMutate = gql`
  mutation repositionTiles($input: RepositionTilesInput!) {
    repositionTiles(input: $input)
  }
`;

export const addFavoriteMutate = gql`
  mutation addFavorite($id: ID!) {
    favorites: addFavorite(id: $id) {
      ${previewAppFields}
    }
  }
`;

export const removeFavoriteMutate = gql`
  mutation removeFavorite($id: ID!) {
    favorites: removeFavorite(id: $id) {
      ${previewAppFields}
    }
  }
`;

export const pageLayoutQuery = gql`
  query pageLayout($id: ID, $type: String!, $limit: Int!) {
    pageLayout(id: $id, type: $type) {
      ... on AppList {
        apps(limit: $limit) {
          ${previewAppFields}
        }
        link {
          type
          resourceId
        }
        title
        type
        isPremium
      }
      ... on BannerList {
        banners {
          title
          text
          background {
            uri
          }
          isPremium
          button {
            text
            link {
              type
              resourceId
            }
          }
        }
        type
      }
    }
  }
`;

export const categoryQuery = gql`
  query category($id: ID!) {
    collection: category(id: $id) {
      id
      name
      apps {
        ${previewAppFields}
      }
    }
  }
`;

export const collectionQuery = gql`
  query collection($id: ID!) {
    collection(id: $id) {
      id
      name
      apps {
        ${previewAppFields}
      }
    }
  }
`;

export const getPagedCollectionQuery = gql`
  query collection($id: ID!, $pageKey: ID, $userFlags: [UserFlag!]) {
    collection(id: $id) {
      id
      name
      flags
      pagedApps(
        pageOptions: {
          pageKey: $pageKey,
          limit: ${appsPerPage},
          numNextPages: 1
        },
        userFlags: $userFlags
      ) {
        apps {
          ${previewAppFields}
        }
        nextPages
      }
    }
  }
`;

export const getPagedCategoryQuery = gql`
  query category($id: ID!, $pageKey: ID, $userFlags: [UserFlag!]) {
    collection: category(id: $id) {
      id
      name
      pagedApps(
        pageOptions: {
          pageKey: $pageKey,
          limit: ${appsPerPage},
          numNextPages: 1
        },
        userFlags: $userFlags
      ) {
        apps {
          ${previewAppFields}
        }
        nextPages
      }
    }
  }
`;

export const hasGalleryGrantQuery = gql`
  query hasGalleryGrant {
    hasGalleryGrant
  }
`;

export const cancelAppInstallMutate = gql`
  mutation cancelAppInstall($id: ID!, $wireId: ID!) {
    cancelAppInstall(id: $id, wireId: $wireId) {
      appId
      installStatus
      installed
      permissions {
        name
        granted
      }
      favorite
      active
      selected
    }
  }
`;
